import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, X, Check, ArrowLeft, Upload } from 'lucide-react';
import { audioApi } from '@/lib/api';
import { getMicErrorMessage } from '@/lib/utils';
import { AUDIO_COLORS, AUDIO_SHAPES, LANGUAGE_FLAGS, COMMON_EMOJIS, EMOJI_CATEGORIES } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';

const GrabarPage = () => {
  const { user, isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(null); // 'record' | 'upload'
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  
  // Classification - emoji ahora es array para múltiples
  const [shape, setShape] = useState('circle');
  const [color, setColor] = useState('blue');
  const [emojis, setEmojis] = useState([]); // Array vacío, sin predeterminado
  const [language, setLanguage] = useState('ES');
  const [allowReplies, setAllowReplies] = useState(true);
  const [emojiCategory, setEmojiCategory] = useState('caras'); // Categoría activa
  const [emojiText, setEmojiText] = useState(''); // Input libre para teclado nativo
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Función para toggle emoji (añadir/quitar)
  const toggleEmoji = (emoji) => {
    if (emojis.includes(emoji)) {
      setEmojis(emojis.filter(e => e !== emoji));
    } else if (emojis.length < 5) { // Máximo 5 emojis
      setEmojis([...emojis, emoji]);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(getMicErrorMessage());
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setDuration(0);
      
      timerRef.current = setInterval(() => {
        setDuration(prev => {
          if (prev >= 59) {
            stopRecording();
            return 59;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      setError(getMicErrorMessage(err));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  // Subir archivo de audio existente
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      setError('Por favor selecciona un archivo de audio válido');
      return;
    }

    const url = URL.createObjectURL(file);
    const audio = new Audio(url);
    
    audio.onloadedmetadata = () => {
      const fileDuration = Math.round(audio.duration);
      
      if (!Number.isFinite(fileDuration)) {
        setError('No se pudo leer la duración del audio. Prueba con otro formato (MP3, WAV, M4A, OGG).');
        URL.revokeObjectURL(url);
        return;
      }
      
      if (fileDuration < 1) {
        setError('El audio debe durar al menos 1 segundo');
        URL.revokeObjectURL(url);
        return;
      }
      
      if (fileDuration > 59) {
        setError('El audio no puede durar más de 59 segundos. Recórtalo antes de subirlo.');
        URL.revokeObjectURL(url);
        return;
      }
      
      setDuration(fileDuration);
      setRecordedBlob(file);
      setRecordedUrl(url);
      setError('');
    };

    audio.onerror = () => {
      setError('No se pudo leer el archivo de audio');
      URL.revokeObjectURL(url);
    };
  };

  const handleUpload = async () => {
    if (!recordedBlob || !Number.isFinite(duration) || duration < 1) {
      setError('El audio debe durar al menos 1 segundo');
      return;
    }

    if (emojis.length === 0) {
      setError('Elige al menos un emoji para identificar el audio de un vistazo 🏷️');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      const extension = recordedBlob.name?.split('.').pop()?.toLowerCase()
        || (recordedBlob.type?.includes('webm') ? 'webm' : 'mp3');
      formData.append('file', recordedBlob, `audio_${Date.now()}.${extension}`);
      formData.append('shape', shape);
      formData.append('color', color);
      formData.append('emoji', emojis.join('') || colorData?.emoji || '💬'); // Emoji por defecto si no se elige ninguno
      formData.append('language', language);
      formData.append('duration_seconds', duration.toString());
      formData.append('allow_replies', allowReplies.toString());

      await audioApi.uploadAudio(formData);
      // Recarga completa para que el feed muestre el audio nuevo
      window.location.href = '/';
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al publicar el audio. Revisa el formato y la duración.');
    } finally {
      setUploading(false);
    }
  };

  const resetAudio = () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setDuration(0);
    setMode(null);
    setStep(1);
  };

  const colorData = AUDIO_COLORS[color];

  // No autenticado
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="text-6xl mb-4">🎙️</div>
          <h2 className="text-xl font-bold text-white mb-2">Inicia sesión para compartir</h2>
          <p className="text-white/60 mb-6">Sube audios, memes, clips de podcast, promociones... ¡todo en 30 segundos!</p>
          <button 
            onClick={login}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-full font-medium"
          >
            Entrar con Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="text-white/60 p-2 -ml-2">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-white font-bold">
            {step === 1 ? 'Añadir Audio' : 'Clasificar'}
          </h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 text-red-200 p-3 rounded-xl mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Step 1: Elegir modo o grabar/subir */}
        {step === 1 && !recordedBlob && (
          <div className="space-y-4">
            {/* Mensaje principal */}
            <div className="text-center mb-2">
              <p className="text-white/70 text-sm">
                Comparte en 30 segundos: memes, clips, promociones, opiniones...
              </p>
            </div>

            {/* Opción 1: Grabar */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center">
                  <Mic size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Grabar ahora</h3>
                  <p className="text-white/50 text-sm">Tu voz, tu opinión, tu momento</p>
                </div>
              </div>
              
              {mode === 'record' ? (
                <div className="text-center py-2">
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto transition-all ${
                      isRecording 
                        ? 'bg-red-500 animate-pulse shadow-[0_0_40px_rgba(239,68,68,0.5)]' 
                        : 'bg-gradient-to-r from-red-500 to-pink-500'
                    }`}
                  >
                    <Mic size={30} className="text-white" />
                  </button>
                  <p className="mt-2 text-2xl font-mono text-white">{duration}s</p>
                  <div className="w-32 h-1.5 bg-white/10 rounded-full mt-2 mx-auto overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-red-500 to-pink-500 rounded-full transition-all" 
                      style={{ width: `${(duration/59)*100}%` }} 
                    />
                  </div>
                  <p className="text-white/40 text-xs mt-2">máx 59s</p>
                </div>
              ) : (
                <button
                  onClick={() => setMode('record')}
                  className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white py-3 rounded-xl font-medium"
                >
                  Empezar a grabar
                </button>
              )}
            </div>

            {/* Opción 2: Subir archivo */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center">
                  <Upload size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Subir audio</h3>
                  <p className="text-white/50 text-sm">Desde tu móvil o PC (máx 59s)</p>
                </div>
              </div>
              
              <label className="block">
                <div className="w-full bg-white/10 hover:bg-white/15 text-white py-3 rounded-xl font-medium text-center cursor-pointer transition-colors">
                  Seleccionar archivo
                </div>
                <input 
                  ref={fileInputRef}
                  type="file" 
                  accept="audio/*"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </label>
            </div>
          </div>
        )}

        {/* Audio grabado/subido - Preview */}
        {step === 1 && recordedBlob && (
          <div className="text-center">
            <div className="w-28 h-28 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mx-auto shadow-xl">
              <Check size={56} className="text-white" />
            </div>
            
            <p className="mt-6 text-2xl font-bold text-white">Audio listo</p>
            <p className="text-white/50 mt-1">{duration} segundos</p>
            
            {recordedUrl && (
              <audio controls src={recordedUrl} className="mx-auto mt-6 w-full max-w-xs" />
            )}
            
            <div className="flex gap-3 mt-8">
              <button
                onClick={resetAudio}
                className="flex-1 bg-white/10 text-white py-3 rounded-xl flex items-center justify-center gap-2"
              >
                <X size={20} />
                Cancelar
              </button>
              <button
                onClick={() => setStep(2)}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2"
              >
                Clasificar
                <Check size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Clasificar */}
        {step === 2 && (
          <div className="space-y-4">
            {/* Shape */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <p className="text-white font-medium mb-3">¿Qué tipo de audio es?</p>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(AUDIO_SHAPES).map(([key, s]) => (
                  <button
                    key={key}
                    onClick={() => setShape(key)}
                    className={`p-3 rounded-xl text-center transition-all relative ${
                      shape === key
                        ? 'bg-green-500/40 ring-2 ring-green-400 scale-105'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    {shape === key && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">✓</span>
                    )}
                    <span className="text-2xl block">{s.icon}</span>
                    <span className="text-white/70 text-xs">{s.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Color */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <p className="text-white font-medium mb-2">Temática</p>
              {color && (
                <p className="text-green-400 text-sm mb-3 flex items-center gap-2">
                  <span>{AUDIO_COLORS[color]?.emoji}</span>
                  <span className="font-medium">{AUDIO_COLORS[color]?.name}</span>
                </p>
              )}
              <div className="grid grid-cols-6 gap-2">
                {Object.entries(AUDIO_COLORS).map(([key, c]) => (
                  <button
                    key={key}
                    onClick={() => setColor(key)}
                    className={`flex flex-col items-center p-2 rounded-xl transition-all ${
                      color === key ? 'ring-2 ring-green-400 bg-green-500/20 scale-105' : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <span 
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-lg mb-1"
                      style={{ backgroundColor: c.hex }}
                    >
                      {c.emoji}
                    </span>
                    <span className="text-white/70 text-[10px] leading-tight text-center">{c.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Emoji - Múltiples con categorías */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white font-medium">Emojis (hasta 5) <span className="text-red-400">*</span></p>
                {emojis.length === 0 && (
                  <span className="text-white/40 text-xs">elige al menos 1</span>
                )}
                {emojis.length > 0 && (
                  <div className="flex gap-1.5 bg-white/10 px-3 py-1.5 rounded-xl">
                    {emojis.map((e, i) => (
                      <span key={i} className="text-2xl">{e}</span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Input libre: teclado nativo de emojis */}
              <input
                type="text"
                value={emojiText}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmojiText(value);
                  const found = value.match(/\p{Extended_Pictographic}(️|‍\p{Extended_Pictographic})*/gu) || [];
                  setEmojis(found.slice(0, 5));
                }}
                placeholder="O escribe emojis con tu teclado 😀"
                className="w-full bg-white/10 text-white rounded-xl px-3 py-2 mb-3 outline-none"
              />

              {/* Categorías */}
              <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
                {Object.keys(EMOJI_CATEGORIES).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setEmojiCategory(cat)}
                    className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${
                      emojiCategory === cat ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/60'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              
              {/* Emojis de la categoría - MÁS GRANDES */}
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                {EMOJI_CATEGORIES[emojiCategory]?.map((e) => (
                  <button
                    key={e}
                    onClick={() => toggleEmoji(e)}
                    className={`w-11 h-11 rounded-xl flex items-center justify-center text-2xl transition-all ${
                      emojis.includes(e) ? 'bg-green-500/50 ring-2 ring-green-400 scale-110' : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
              
              {emojis.length > 0 && (
                <button 
                  onClick={() => setEmojis([])}
                  className="mt-2 text-xs text-white/40 hover:text-white/60"
                >
                  Limpiar selección
                </button>
              )}
            </div>

            {/* Language */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <p className="text-white font-medium mb-3">Idioma</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(LANGUAGE_FLAGS).map(([code, langData]) => (
                  <button
                    key={code}
                    onClick={() => setLanguage(code)}
                    className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl transition-all ${
                      language === code ? 'bg-green-500/50 ring-2 ring-green-400 scale-110' : 'bg-white/5'
                    }`}
                  >
                    {langData.flag}
                  </button>
                ))}
              </div>
            </div>

            {/* Allow replies */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Permitir réplicas</p>
                  <p className="text-white/50 text-sm">Otros pueden responder con audio</p>
                </div>
                <button
                  onClick={() => setAllowReplies(!allowReplies)}
                  className={`w-14 h-8 rounded-full transition-colors ${
                    allowReplies ? 'bg-green-500' : 'bg-white/20'
                  }`}
                >
                  <div className={`w-6 h-6 bg-white rounded-full transition-transform mx-1 ${
                    allowReplies ? 'translate-x-6' : ''
                  }`} />
                </button>
              </div>
            </div>

            {/* Preview & Submit - Badge visual completo */}
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur rounded-2xl p-4 border border-purple-500/30">
              <p className="text-white/60 text-sm mb-3">Vista previa del badge</p>
              <div className="flex items-center gap-4">
                {/* Badge visual: Forma + Color + Emojis + Bandera */}
                <div className="relative">
                  <div 
                    className="w-20 h-20 rounded-xl flex flex-col items-center justify-center border-2"
                    style={{ 
                      backgroundColor: colorData?.hex || '#6366F1',
                      borderColor: colorData?.hex ? colorData.hex + '80' : '#6366F180'
                    }}
                  >
                    <span className="text-xs text-white/80 mb-0.5">{AUDIO_SHAPES[shape]?.icon}</span>
                    <span className="text-2xl leading-none">{emojis.join('') || '💬'}</span>
                  </div>
                  {/* Bandera debajo */}
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white/20 backdrop-blur px-1.5 py-0.5 rounded text-sm">
                    {LANGUAGE_FLAGS[language]?.flag || '🇪🇸'}
                  </div>
                </div>
                <div>
                  <p className="text-white font-medium">{colorData?.emoji} {colorData?.name}</p>
                  <p className="text-white/50 text-sm">{duration}s • {AUDIO_SHAPES[shape]?.name}</p>
                  <p className="text-white/40 text-xs mt-1">{emojis.length} emoji(s) • {LANGUAGE_FLAGS[language]?.name}</p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setStep(1)}
                className="flex-1 bg-white/10 text-white py-4 rounded-xl"
              >
                Atrás
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-4 rounded-xl font-medium disabled:opacity-50"
              >
                {uploading ? 'Publicando...' : 'Publicar'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default GrabarPage;
